import { FC, useState, useCallback } from 'react';
import styled from 'styled-components';
import { themeColors, themeSizes } from '../../../styles/globalStyleVars';
import {
  IconBook2,
  IconChevronDown,
  IconChevronUp,
  IconCircleCheck,
} from '@tabler/icons-react';
import RelativeTime from '../../RelativeTime/RelativeTime';
import { AlertInterface } from '../../../interfaces/AlertInterface';
import { callEvent } from '../../../utils/Amplitude/AmplitudeUtilities';
import { TypographyLineLimit } from '../../Styled/TypographyElements';
import { TypographyStyled } from '../../Styled/Typography';
import { Button, Divider, LinkButton } from '@cfa/react-core';

interface Props {
  alert: AlertInterface;
  showSummaryByDefault?: boolean;
}

const AlertDiv = styled.div`
  border: 1px solid ${themeColors.colorGray_1};
  border-radius: ${themeSizes.sm};
  width: 100%;
  margin-bottom: ${themeSizes.lg};
  padding: ${themeSizes.lg};
  padding-bottom: ${themeSizes.smMd};
  display: flex;
  flex-direction: column;

  &:not(:has(~ .alert-item)) {
    margin-bottom: 56px;
  }
`;

const AlertStatus = styled.div`
  display: flex;
  justify-content: space-between;

  > div {
    color: ${themeColors.colorGray_6};
    font-weight: 500;
    display: flex;
    margin-bottom: ${themeSizes.smMd};

    > svg {
      margin-right: ${themeSizes.sm};
    }

    > p {
      color: ${themeColors.errorColor};
    }
  }
`;

const AlertSummaryText = styled(TypographyStyled)`
  color: ${themeColors.colorBlack};
  margin-top: 9px;
  white-space: pre-line;
`;

const SeeMoreButton = styled(Button)`
  padding: 0;
  display: flex;
  display: -webkit-flex;
  justify-content: flex-end;
  text-decoration: underline;
  -webkit-justify-content: flex-end;
  -webkit-align-self: flex-end;
  align-self: flex-end;
`;

const LinkToURLButton = styled(LinkButton)`
  padding: 0;
  min-width: auto;
  margin-top: ${themeSizes.sm};

  > svg {
    margin-right: ${themeSizes.sm};
  }
`;

export const ExpiredAlertItem: FC<Props> = ({
  alert,
  showSummaryByDefault,
}) => {
  const [overflowingSummary, setOverflowingSummary] = useState<boolean>(false);
  const [expandedSummary, setExpandedSummary] = useState<boolean>(false);

  const summaryRef = useCallback(
    (node: HTMLParagraphElement | null) => {
      if (node && showSummaryByDefault) {
        const clientHeight = node.clientHeight;
        if (clientHeight && clientHeight > 240) {
          setOverflowingSummary(true);
        }
      }
    },
    [showSummaryByDefault]
  );

  const handleExpandSummary = () => {
    const willExpand = !expandedSummary;
    setExpandedSummary(willExpand);

    if (willExpand) {
      callEvent('expand alert', {
        'alert id': alert.id,
        title: alert.title,
      });
    }
  };

  const renderSummaryExpanded = () => {
    const summaryContent =
      overflowingSummary && !expandedSummary ? (
        <TypographyLineLimit
          $fontColor={themeColors.colorBlack}
          $lineLimit={10}
          style={{
            maxHeight: '240px',
            marginTop: '9px',
            whiteSpace: 'pre-line',
          }}
          data-testid='alert-summary'
        >
          {alert.summary}
        </TypographyLineLimit>
      ) : (
        <AlertSummaryText
          ref={summaryRef}
          text={alert.summary ?? ''}
          data-testid='alert-summary'
        />
      );

    return (
      <>
        {summaryContent}
        {overflowingSummary && (
          <SeeMoreButton
            variant='text'
            color='secondary'
            size='sm'
            onPress={handleExpandSummary}
          >
            {expandedSummary ? 'See Less' : 'See More'}
          </SeeMoreButton>
        )}
        {alert.url && (
          <>
            <Divider style={{ margin: `${themeSizes.smMd} 0 0 0` }} />
            <LinkToURLButton
              variant='text'
              color='secondary'
              href={alert.url}
              target='_blank'
              rel='noreferrer'
              onPress={() =>
                callEvent('alert link', {
                  'alert id': alert.id,
                  title: alert.title,
                })
              }
            >
              <IconBook2 size={16} />
              <TypographyStyled
                text='Read More'
                color={themeColors.secondaryBlue}
                fontWeight='bold'
              />
            </LinkToURLButton>
          </>
        )}
      </>
    );
  };

  const renderSummaryCollapsed = () => {
    if (!alert.summary) return null;

    const ChevronIcon = expandedSummary ? IconChevronUp : IconChevronDown;

    return (
      <>
        <SeeMoreButton
          variant='text'
          color='secondary'
          size='sm'
          onPress={handleExpandSummary}
        >
          {expandedSummary ? 'Hide' : 'Show'} Details{' '}
          <ChevronIcon
            size={themeSizes.lg}
            style={{ margin: `0 0 0 ${themeSizes.xs}` }}
          />
        </SeeMoreButton>
        {expandedSummary && (
          <AlertSummaryText
            text={alert.summary ?? ''}
            data-testid='alert-summary'
          />
        )}
      </>
    );
  };

  return (
    <AlertDiv key={alert.id} className='alert-item'>
      <AlertStatus>
        <div>
          <IconCircleCheck size={26} aria-label='Expired check icon' /> Expired
        </div>
        <TypographyStyled data-testid='alert-time'>
          <RelativeTime date={alert.updatedOn} />
        </TypographyStyled>
      </AlertStatus>
      <TypographyLineLimit
        $fontColor={themeColors.colorBlack}
        $lineLimit={3}
        variant='h4'
        style={{ maxHeight: '96px' }}
        data-testid='alert-heading'
      >
        {alert.title}
      </TypographyLineLimit>

      {showSummaryByDefault
        ? renderSummaryExpanded()
        : renderSummaryCollapsed()}
    </AlertDiv>
  );
};
